/**
 * api.ts — lớp gọi API cho ẢNH IN của đơn.
 *
 * Hai nửa, hai quyền khác hẳn nhau, đừng trộn:
 *
 * 1. NHÂN VIÊN (4 endpoint `/orders/:id/print-photos*`) — đi qua client `api`
 *    đã xác thực, hoặc `postFormAuthed` cho lượt nạp multipart.
 * 2. KHÁCH cầm link (2 endpoint `/print-shares/:token*`) — KHÔNG có phiên đăng
 *    nhập nào; token trên đường dẫn là quyền duy nhất. Dùng `fetch` trần chứ
 *    KHÔNG dùng client đã xác thực: gắn credential vào chỉ tổ gửi cookie phiên
 *    của nhân viên nào đó vừa dùng chung máy (cùng lý do đã ghi ở
 *    `features/album-share/api.ts`).
 *
 * Đơn của khách suy ra TỪ TOKEN ở phía máy chủ, nên không hàm khách nào ở đây
 * nhận id đơn.
 */

import { api } from "@/lib/api/client";
import { postFormAuthed } from "@/lib/api/authed-fetch";
import { serverErrorMessage } from "@/lib/api/error-message";
import type { components } from "@/lib/api/generated/schema";

export type PrintPhoto = components["schemas"]["PrintPhoto"];
export type PrintPhotoStaffView = components["schemas"]["PrintPhotoStaffView"];
export type PrintPhotoUploadResult = components["schemas"]["PrintPhotoUploadResult"];
export type CustomerAlbum = components["schemas"]["CustomerAlbum"];
export type SignedMediaURL = components["schemas"]["SignedMediaURL"];

/** Cỡ ảnh xin từ đường đọc. `orig` chỉ dùng khi biến thể chưa sinh xong. */
export type PrintPhotoVariant = "thumb" | "md" | "lg" | "orig";

const apiBaseUrl = import.meta.env.VITE_API_URL ?? "http://localhost:8080/api/v1";

export const printPhotoKeys = {
  /**
   * Tiền tố của MỌI trang ảnh in thuộc một đơn.
   *
   * Dùng để vô hiệu hoá cache: `invalidateQueries` khớp theo tiền tố, nên
   * truyền khoá này là quét sạch mọi trang. Truyền khoá của đúng một trang thì
   * các trang khác giữ dữ liệu cũ — nạp ảnh xong mà lưới không đổi.
   */
  orderPhotos: (orderId: string) => ["order-print-photos", orderId] as const,
  /**
   * Một trang cụ thể. Cỡ trang nằm TRONG khoá: đổi cỡ trang mà khoá không đổi
   * thì react-query trả lại đúng trang cũ.
   */
  orderPhotosPage: (orderId: string, offset: number, limit: number) =>
    ["order-print-photos", orderId, offset, limit] as const,
  url: (orderId: string, photoId: string, variant: PrintPhotoVariant) =>
    ["print-photo-url", orderId, photoId, variant] as const,
  customer: (token: string) => ["print-share-customer", token] as const,
};

/** Giữ nguyên câu tiếng Việt máy chủ đã viết; lời tiếng Anh đi qua bộ dịch chung. */
function staffError(payload: unknown, fallback: string): Error {
  return new Error(serverErrorMessage(payload, fallback));
}

async function uploadError(resp: Response, fallback: string): Promise<Error> {
  try {
    return staffError(await resp.json(), fallback);
  } catch {
    // Thân không phải JSON — rơi xuống câu chung.
    return new Error(fallback);
  }
}

// ────────────────────────────────────────────────────────────── nhân viên

/** Một trang ảnh in của đơn. */
export async function listOrderPrintPhotos(
  orderId: string,
  page: { offset?: number; limit?: number } = {},
): Promise<PrintPhotoStaffView> {
  const { data, error } = await api.GET("/orders/{id}/print-photos", {
    params: {
      path: { id: orderId },
      query: {
        ...(page.offset ? { offset: page.offset } : {}),
        ...(page.limit ? { limit: page.limit } : {}),
      },
    },
  });
  if (error || !data) throw staffError(error, "Không tải được ảnh in của đơn");
  return data;
}

/**
 * Nạp một mẻ ảnh in lên đơn.
 *
 * Đi qua `postFormAuthed` chứ không qua client sinh từ openapi: thân là
 * FormData, thứ client đó không có lược đồ để mô tả.
 */
export async function addOrderPrintPhotos(
  orderId: string,
  files: File[],
): Promise<PrintPhotoUploadResult> {
  const form = new FormData();
  for (const f of files) form.append("files", f);
  const resp = await postFormAuthed(`${apiBaseUrl}/orders/${orderId}/print-photos`, form);
  if (!resp.ok) throw await uploadError(resp, "Không nạp được ảnh in lên đơn");
  return (await resp.json()) as PrintPhotoUploadResult;
}

/** Gỡ một ảnh in khỏi đơn. Máy chủ dọn cả tệp trong kho. */
export async function deleteOrderPrintPhoto(orderId: string, photoId: string): Promise<void> {
  const { error, response } = await api.DELETE("/orders/{id}/print-photos/{photoId}", {
    params: { path: { id: orderId, photoId } },
  });
  if (!response.ok) throw staffError(error, "Không gỡ được ảnh in");
}

/** Gỡ nhiều ảnh in một lượt (danh sách mã, hoặc cả đơn); máy chủ dọn luôn tệp trong kho. */
export async function bulkDeleteOrderPrintPhotos(
  orderId: string,
  body: { photoIds: string[] } | { all: true },
): Promise<{ deletedCount: number; skippedCount: number }> {
  const { data, error } = await api.POST("/orders/{id}/print-photos/bulk-delete", {
    params: { path: { id: orderId } },
    body,
  });
  if (!data) throw staffError(error, "Không gỡ được ảnh in");
  return data;
}

/**
 * Xin URL đọc một ảnh in.
 *
 * Ảnh nằm trong kho riêng tư, nên KHÔNG được trỏ `<img src>` thẳng vào route
 * của BE: FE và API khác origin, cookie phiên là SameSite=Lax nên trình duyệt
 * không gửi kèm khi tải ảnh và nhận 401. Lấy URL đã ký ở đây rồi mới đặt vào
 * `src`.
 */
export async function getPrintPhotoURL(
  orderId: string,
  photoId: string,
  variant: PrintPhotoVariant = "thumb",
): Promise<SignedMediaURL> {
  const { data, error } = await api.GET("/orders/{id}/print-photos/{photoId}/url", {
    params: { path: { id: orderId, photoId }, query: { v: variant } },
  });
  if (error || !data) throw staffError(error, "Không mở được ảnh in");
  return data;
}

// ────────────────────────────────────────────────────────────────── khách

/**
 * [Khách] Mở bộ ảnh in bằng token.
 *
 * Không qua client đã đăng nhập — xem đầu file. Máy chủ cố tình trả CÙNG MỘT
 * 404 cho token sai / hết hạn / đã thu hồi / sai phạm vi; FE giữ nguyên cách
 * nói đó, không đoán hộ khách lý do nào.
 */
export async function openCustomerPrintAlbum(token: string): Promise<CustomerAlbum> {
  const resp = await fetch(`${apiBaseUrl}/print-shares/${encodeURIComponent(token)}`, {
    headers: { Accept: "application/json" },
  });
  if (!resp.ok) {
    if (resp.status === 404) {
      throw new Error(
        "Link này không còn xem được. Chị liên hệ studio để được gửi lại link mới giúp em ạ.",
      );
    }
    if (resp.status === 429) {
      throw new Error("Nhiều lượt xem quá, chị đợi một chút rồi mở lại giúp em ạ.");
    }
    throw new Error("Không mở được ảnh in, chị thử lại giúp em ạ.");
  }
  return (await resp.json()) as CustomerAlbum;
}

/** [Khách] Tải cả bộ ảnh in về dạng zip. */
export async function downloadCustomerPrintZip(token: string, filename: string): Promise<void> {
  const resp = await fetch(
    `${apiBaseUrl}/print-shares/${encodeURIComponent(token)}/download.zip`,
    { headers: { Accept: "application/zip" } },
  );
  if (!resp.ok) {
    if (resp.status === 404) {
      throw new Error("Album ảnh in này chưa tải được. Chị liên hệ studio để được hỗ trợ giúp em ạ.");
    }
    if (resp.status === 429) {
      throw new Error("Nhiều lượt tải quá, chị đợi một chút rồi thử lại giúp em ạ.");
    }
    throw new Error("Không tải được ảnh in, chị thử lại giúp em ạ.");
  }

  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.append(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
